/**
 * FE#194 — decide whether the Free-plan tile should render an ENABLED
 * "Cancel Subscription / Downgrade to Free" control.
 *
 * Background: the cancel action is wired to the Free-tile ChargebeeButton
 * (chargebee-button.tsx fires cancelSubscriptionThunk only when plan==='free'),
 * but that button was previously rendered ONLY for `isCurrentPlan` and disabled
 * ("Current Plan"). A paying customer therefore had no reachable cancel control
 * and support had to cancel via Chargebee scripts. This predicate restores the
 * missing branch: show an enabled cancel CTA when the caller is NOT on the Free
 * plan but DOES have an active paid subscription to cancel.
 *
 * Kept as a pure function so the decision is unit-testable without rendering the
 * (large) plan-selection component.
 */
export interface CancelCtaInput {
  /** Is the Free tile the user's current plan? (true → they're already free) */
  isCurrentPlan: boolean;
  /** Does the user hold a paid subscription? (activeSubscription.order > 0) */
  hasPaidSubscription: boolean;
  /** Chargebee subscription status, e.g. 'active' | 'non_renewing' | 'cancelled'. */
  activeStatus?: string | null;
}

export const shouldShowFreeTileCancelCta = ({
  isCurrentPlan,
  hasPaidSubscription,
  activeStatus,
}: CancelCtaInput): boolean => {
  // Already on Free → nothing to cancel (the disabled "Current Plan" chip shows instead).
  if (isCurrentPlan) return false;
  // Only offer cancel when there is a paid subscription that is actually live.
  // A non_renewing/cancelled sub is already winding down — showing "Cancel" again
  // would be misleading (and the backend treats it as a no-op).
  if (!hasPaidSubscription) return false;
  // #1182 — an `in_trial` sub is live and cancellable (the backend's cancellable set
  // includes in_trial); hiding Cancel for trials left trial users with no way out here
  // (the hosted portal's end-of-term cancel is rejected for trials).
  return activeStatus === 'active' || activeStatus === 'in_trial';
};
